import React, { useCallback, useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

/**
 * Google authorization button backed by the GIS OAuth2 code client.
 * This uses the popup auth-code flow so the backend can exchange the
 * code for revocable Google tokens.
 *
 * Two bugs the previous implementation had, and how this one avoids them:
 *
 * 1. The GIS code client was built in `useMemo([onCodeReceived, onError,
 *    prompt])`. On first mount the GIS script is not loaded yet, so the
 *    memoized client is `null`. When the script later loads,
 *    `setIsReady(true)` only re-renders this component -- the parent
 *    (`AuthModal`) does not re-render, so the inline `onCodeReceived`
 *    prop does not change reference, so the memo never re-runs and
 *    `codeClient` stays `null` forever. Clicking the button silently
 *    no-opped because `isReady` was true (no error fired) but
 *    `codeClient` was null. Now the client is built inside a
 *    `useEffect` keyed on `isReady` and stored in a ref, so script
 *    load reliably constructs it once. Latest props are dispatched via
 *    `callbacksRef` so the client never needs to be rebuilt for prop
 *    churn.
 *
 * 2. GIS only invokes `callback` when the auth flow completes. If the
 *    user closes/cancels the popup, our `isSubmitting` state used to
 *    stay `true` forever and the button stayed disabled until a page
 *    reload. GIS provides `error_callback` for exactly this case
 *    (popup closed, blocked, or failed). We pass it now and reset
 *    `isSubmitting` so the user can click "Continue with Google"
 *    again. We also wrap `requestCode()` in try/catch as a
 *    defense-in-depth in case the popup blocker rejects synchronously.
 */
const GoogleSignin = ({
    disabled = false,
    label = "Continue with Google",
    onCodeReceived,
    onError,
    prompt = "consent",
}) => {
    const [isReady, setIsReady] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Always-latest callbacks. The GIS client is built once per
    // (isReady, prompt) and held in a ref; invoking through this
    // mutable ref keeps the parent's inline callbacks fresh without
    // forcing a costly rebuild of the client every render.
    const callbacksRef = useRef({ onCodeReceived, onError })
    useEffect(() => {
        callbacksRef.current = { onCodeReceived, onError }
    }, [onCodeReceived, onError])

    const codeClientRef = useRef(null)

    useEffect(() => {
        const markReady = () => {
            const oauthClient = window.google?.accounts?.oauth2
            setIsReady(Boolean(oauthClient))
        }

        if (window.google?.accounts?.oauth2) {
            markReady()
            return undefined
        }

        let script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]')
        if (!script) {
            script = document.createElement("script")
            script.src = "https://accounts.google.com/gsi/client"
            script.async = true
            script.defer = true
            script.onload = markReady
            document.body.appendChild(script)
        } else {
            script.addEventListener("load", markReady, { once: true })
        }

        return undefined
    }, [])

    // Build the GIS client when (and only after) the script has loaded
    // and the env var is present. Reset isSubmitting and notify the
    // parent on cancel/error via `error_callback` -- this is the fix
    // for the "button stays grayed-out after closing the popup" bug.
    useEffect(() => {
        if (!isReady || codeClientRef.current) {
            return
        }
        const oauthClient = window.google?.accounts?.oauth2
        if (!oauthClient || !process.env.REACT_APP_GOOGLE_CLIENT_ID) {
            return
        }
        codeClientRef.current = oauthClient.initCodeClient({
            callback: async (response) => {
                if (response.error) {
                    setIsSubmitting(false)
                    if (callbacksRef.current.onError) {
                        callbacksRef.current.onError(response.error)
                    }
                    return
                }

                try {
                    if (callbacksRef.current.onCodeReceived) {
                        await callbacksRef.current.onCodeReceived({ code: response.code })
                    }
                } finally {
                    setIsSubmitting(false)
                }
            },
            error_callback: (error) => {
                // Always re-enable the button. `popup_closed` is an
                // explicit user action (closed the auth tab), so we
                // do not surface it as an error message; other failure
                // types (popup_failed_to_open, unknown, etc.) propagate.
                setIsSubmitting(false)
                const type = error && error.type ? error.type : "google_failed"
                if (type !== "popup_closed" && callbacksRef.current.onError) {
                    callbacksRef.current.onError(type)
                }
            },
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            prompt,
            scope: "openid email profile",
            select_account: true,
            ux_mode: "popup",
        })
    }, [isReady, prompt])

    const buttonDisabled = disabled || !isReady || isSubmitting

    const handleClick = useCallback(() => {
        if (buttonDisabled) {
            return
        }
        const codeClient = codeClientRef.current
        if (!codeClient) {
            // Should not happen once `isReady` is true, but guard so a
            // missed init does not silently no-op the button.
            if (callbacksRef.current.onError) {
                callbacksRef.current.onError("google_not_ready")
            }
            return
        }

        setIsSubmitting(true)
        try {
            codeClient.requestCode()
        } catch (err) {
            // Popup blocked or other synchronous failure: re-enable the
            // button so the user can try again rather than getting
            // stuck on a "depressed"-looking control.
            setIsSubmitting(false)
            if (callbacksRef.current.onError) {
                callbacksRef.current.onError("google_request_failed")
            }
        }
    }, [buttonDisabled])

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={buttonDisabled}
            aria-busy={isSubmitting || undefined}
            className={`flex w-full items-center justify-center gap-3 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60`}
        >
            {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" />
            ) : (
                <svg aria-hidden="true" className="size-5" viewBox="0 0 24 24">
                    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.2-.9 2.2-1.9 2.9l3.1 2.4c1.8-1.7 2.8-4.2 2.8-7.1 0-.7-.1-1.4-.2-2.1H12z" />
                    <path fill="#34A853" d="M12 22c2.7 0 5-0.9 6.6-2.5l-3.1-2.4c-.9.6-2 .9-3.5.9-2.7 0-5-1.8-5.8-4.3H3v2.7C4.6 19.7 8 22 12 22z" />
                    <path fill="#4A90E2" d="M6.2 13.7c-.2-.6-.3-1.2-.3-1.7s.1-1.2.3-1.7V7.6H3C2.4 8.8 2 10.3 2 12s.4 3.2 1 4.4l3.2-2.7z" />
                    <path fill="#FBBC05" d="M12 5.1c1.5 0 2.8.5 3.8 1.5l2.8-2.8C17 2.3 14.7 1.4 12 1.4 8 1.4 4.6 3.7 3 7.6l3.2 2.7c.8-2.5 3.1-4.2 5.8-4.2z" />
                </svg>
            )}
            <span>{label}</span>
        </button>
    )
}

export default GoogleSignin
